import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

// Layouts
import { CustomerLayout } from '@shared/components/layout/CustomerLayout';
import { AdminLayout } from '@shared/components/layout/AdminLayout';
import { AuthLayout } from '@shared/components/layout/AuthLayout';

// Guards
import { AuthGuard } from '@core/guards/AuthGuard';
import { AdminGuard } from '@core/guards/AdminGuard';
import { GuestGuard } from '@core/guards/GuestGuard';

// Error pages
const NotFoundPage = lazy(() => import('@features/error/NotFoundPage'));

// Auth pages
const LoginPage = lazy(() => import('@features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@features/auth/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@features/auth/pages/ResetPasswordPage'));
const AdminLoginPage = lazy(() => import('@features/auth/pages/AdminLoginPage'));

// Customer pages
const HomePage = lazy(() => import('@features/customer/home/pages/HomePage'));
const ShopPage = lazy(() => import('@features/customer/products/pages/ShopPage'));
const ProductDetailPage = lazy(
    () => import('@features/customer/products/pages/ProductDetailPage')
);
// const BrandsListPage = lazy(() => import('@features/customer/brands/pages/BrandsListPage'));
const CategoriesListPage = lazy(() => import('@features/customer/brands/pages/CategoriesListPage')); // Dynamic category listing
// const BrandDetailPage = lazy(() => import('@features/customer/brands/pages/BrandDetailPage'));
const CartPage = lazy(() => import('@features/customer/cart/pages/CartPage'));
const CheckoutPage = lazy(() => import('@features/customer/cart/pages/CheckoutPage'));
const OrderConfirmationPage = lazy(
    () => import('@features/customer/cart/pages/OrderConfirmationPage')
);
const AboutUsPage = lazy(() => import('@features/customer/about/pages/AboutUsPage'));
const StaticPage = lazy(() => import('@features/customer/static-pages/pages/StaticPage'));
// const BespokeServicesPage = lazy(
//     () => import('@features/customer/bespoke/pages/BespokeServicesPage')
// );
// const BespokeServiceDetailPage = lazy(
//     () => import('@features/customer/bespoke/pages/BespokeServiceDetailPage')
// );
const ContactUsPage = lazy(() => import('@features/customer/contact/pages/ContactUsPage'));
const UserDashboardPage = lazy(
    () => import('@features/customer/account/pages/UserDashboardPage')
);

// Admin pages
const DashboardHomePage = lazy(
    () => import('@features/admin/dashboard/pages/DashboardHomePage')
);
const AdminDashboardPage = lazy(
    () => import('@features/admin/dashboard/pages/DashboardPage')
);
const AdminPlaceholderPage = lazy(
    () => import('@features/admin/dashboard/pages/DashboardPlaceholderPage')
);
const AdminOrdersPage = lazy(() => import('@features/admin/orders/pages/OrdersListPage'));
const AdminOrderDetailPage = lazy(
    () => import('@features/admin/orders/pages/OrderDetailPage')
);
const AdminProductsPage = lazy(
    () => import('@features/admin/products/pages/ProductsListPage')
);
const AdminAddProductPage = lazy(
    () => import('@features/admin/products/pages/ProductCreatePage')
);
const AdminEditProductPage = lazy(
    () => import('@features/admin/products/pages/ProductEditPage')
);
const AdminCategoriesPage = lazy(
    () => import('@features/admin/categories/pages/CategoriesListPage')
);
const AdminAddCategoryPage = lazy(
    () => import('@features/admin/categories/pages/CategoryCreatePage')
);
const AdminEditCategoryPage = lazy(
    () => import('@features/admin/categories/pages/CategoryEditPage')
);
const AdminSubCategoriesPage = lazy(
    () => import('@features/admin/subcategories/pages/SubCategoriesListPage')
);
const AdminAddSubCategoryPage = lazy(
    () => import('@features/admin/subcategories/pages/SubCategoryCreatePage')
);
const AdminEditSubCategoryPage = lazy(
    () => import('@features/admin/subcategories/pages/SubCategoryEditPage')
);
const AdminBrandsPage = lazy(() => import('@features/admin/brands/pages/BrandsListPage'));
const AdminAddBrandPage = lazy(() => import('@features/admin/brands/pages/BrandCreatePage'));
const AdminEditBrandPage = lazy(() => import('@features/admin/brands/pages/BrandEditPage'));
const AdminCarriersPage = lazy(() => import('@features/admin/carriers/pages/CarriersListPage'));
const AdminAddCarrierPage = lazy(() => import('@features/admin/carriers/pages/CarrierCreatePage'));
const AdminEditCarrierPage = lazy(() => import('@features/admin/carriers/pages/CarrierEditPage'));
const AdminSlidersPage = lazy(() => import('@features/admin/sliders/pages/SlidersListPage'));
const AdminAddSliderPage = lazy(() => import('@features/admin/sliders/pages/SliderCreatePage'));
const AdminEditSliderPage = lazy(() => import('@features/admin/sliders/pages/SliderEditPage'));
// const AdminBespokeServicesPage = lazy(
//     () => import('@features/admin/bespoke/pages/BespokeServicesListPage')
// );
// const AdminBespokeServiceFormPage = lazy(
//     () => import('@features/admin/bespoke/pages/BespokeServiceFormPage')
// );
const AdminProfilePage = lazy(() => import('@features/admin/profile/pages/ProfilePage'));
const AdminSalesPage = lazy(() => import('@features/admin/sales/pages/SalesListPage'));
const AdminAddSalePage = lazy(() => import('@features/admin/sales/pages/SaleCreatePage'));
const AdminEditSalePage = lazy(() => import('@features/admin/sales/pages/SaleEditPage'));
const AdminInventoryPage = lazy(
    () => import('@features/admin/inventory/pages/InventoryPage')
);
const AdminAboutUsPage = lazy(() => import('@features/admin/about-us/pages/AboutUsEditPage'));
const AdminStaticPagesPage = lazy(() => import('@features/admin/pages/pages/StaticPagesListPage'));
const AdminAddStaticPage = lazy(() => import('@features/admin/pages/pages/StaticPageCreatePage'));
const AdminEditStaticPage = lazy(() => import('@features/admin/pages/pages/StaticPageEditPage'));
const AdminSizeGuidesPage = lazy(() => import('@features/admin/size-guides/pages/SizeGuidesListPage'));
const AdminAddSizeGuidePage = lazy(() => import('@features/admin/size-guides/pages/SizeGuideCreatePage'));
const AdminEditSizeGuidePage = lazy(() => import('@features/admin/size-guides/pages/SizeGuideEditPage'));
const AdminEmailSettingsPage = lazy(
    () => import('@features/admin/settings/pages/EmailSettingsPage')
);
const AdminTaxSettingsPage = lazy(
    () => import('@features/admin/settings/pages/TaxSettingsPage')
);
const AdminShippingSettingsPage = lazy(
    () => import('@features/admin/settings/pages/ShippingSettingsPage')
);
const AdminShippingIntegrationPage = lazy(
    () => import('@features/admin/settings/pages/ShippingIntegrationPage')
);
const AdminSiteSettingsPage = lazy(
    () => import('@features/admin/settings/pages/SiteSettingsPage')
);



/**
 * Route configuration for the application
 */
const routes: RouteObject[] = [
    // Customer-facing routes
    {
        path: '/',
        element: <CustomerLayout />,
        children: [
            { index: true, element: <HomePage /> },
            { path: 'shop', element: <ShopPage /> },
            { path: 'product/:id', element: <ProductDetailPage /> },
            { path: 'brands', element: <Navigate to="/categories" replace /> },
            { path: 'brands/:id', element: <Navigate to="/categories" replace /> },
            { path: 'categories', element: <CategoriesListPage /> },
            { path: 'about-us', element: <AboutUsPage /> },
            { path: 'contact-us', element: <ContactUsPage /> },
            { path: 'terms', element: <StaticPage routeSlug="terms" /> },
            { path: 'privacy', element: <StaticPage routeSlug="privacy" /> },
            { path: 'shipping-policy', element: <StaticPage routeSlug="shipping-policy" /> },
            { path: 'size-guide', element: <StaticPage routeSlug="size-guide" /> },
            // { path: 'bespoke-services', element: <BespokeServicesPage /> },
            // { path: 'bespoke-services/:slug', element: <BespokeServiceDetailPage /> },
            { path: 'cart', element: <CartPage /> },
            { path: 'checkout', element: <CheckoutPage /> },
            { path: 'order-confirmation/:orderId', element: <OrderConfirmationPage /> },
            {
                path: 'my-account',
                element: (
                    <AuthGuard>
                        <UserDashboardPage />
                    </AuthGuard>
                ),
            },
        ],
    },

    // Authentication routes
    {
        path: '/authentication',
        element: (
            <GuestGuard>
                <AuthLayout />
            </GuestGuard>
        ),
        children: [
            { index: true, element: <LoginPage /> },
            { path: 'sign-up', element: <RegisterPage /> },
            { path: 'forgot-password', element: <ForgotPasswordPage /> },
            { path: 'reset-password', element: <ResetPasswordPage /> },
        ],
    },

    // Admin login
    {
        path: '/dashboard/admin-login',
        element: <AdminLoginPage />,
    },
    {
        path: '/admin/login',
        element: <Navigate to="/dashboard/admin-login" replace />,
    },

    // Admin dashboard routes
    {
        path: '/dashboard',
        element: (
            <AdminGuard>
                <AdminLayout />
            </AdminGuard>
        ),
        children: [
            { index: true, element: <Navigate to="home" replace /> },
            { path: 'home', element: <DashboardHomePage /> },
            { path: 'home-old', element: <AdminDashboardPage /> },
            { path: 'profile', element: <AdminProfilePage /> },
            { path: 'orders', element: <AdminOrdersPage /> },
            { path: 'orders/:id', element: <AdminOrderDetailPage /> },

            // Products
            { path: 'products', element: <Navigate to="list" replace /> },
            { path: 'products/list', element: <AdminProductsPage /> },
            { path: 'products/add', element: <AdminAddProductPage /> },
            { path: 'products/edit/:id', element: <AdminEditProductPage /> },

            // Categories
            { path: 'category', element: <Navigate to="list" replace /> },
            { path: 'category/list', element: <AdminCategoriesPage /> },
            { path: 'category/add', element: <AdminAddCategoryPage /> },
            { path: 'category/edit/:id', element: <AdminEditCategoryPage /> },

            // Sub-Categories
            { path: 'sub-category', element: <Navigate to="list" replace /> },
            { path: 'sub-category/list', element: <AdminSubCategoriesPage /> },
            { path: 'sub-category/add', element: <AdminAddSubCategoryPage /> },
            { path: 'sub-category/edit/:id', element: <AdminEditSubCategoryPage /> },

            // Brands
            { path: 'brands', element: <Navigate to="list" replace /> },
            { path: 'brands/list', element: <AdminBrandsPage /> },
            { path: 'brands/add', element: <AdminAddBrandPage /> },
            { path: 'brands/edit/:id', element: <AdminEditBrandPage /> },

            // Carriers
            { path: 'carriers', element: <Navigate to="list" replace /> },
            { path: 'carriers/list', element: <AdminCarriersPage /> },
            { path: 'carriers/add', element: <AdminAddCarrierPage /> },
            { path: 'carriers/edit/:id', element: <AdminEditCarrierPage /> },

            // Sales
            { path: 'sales', element: <Navigate to="list" replace /> },
            { path: 'sales/list', element: <AdminSalesPage /> },
            { path: 'sales/add', element: <AdminAddSalePage /> },
            { path: 'sales/edit/:id', element: <AdminEditSalePage /> },

            // Sliders
            { path: 'sliders', element: <Navigate to="list" replace /> },
            { path: 'sliders/list', element: <AdminSlidersPage /> },
            { path: 'sliders/add', element: <AdminAddSliderPage /> },
            { path: 'sliders/edit/:id', element: <AdminEditSliderPage /> },

            // Bespoke Services
            // { path: 'bespoke-services', element: <Navigate to="list" replace /> },
            // { path: 'bespoke-services/list', element: <AdminBespokeServicesPage /> },
            // { path: 'bespoke-services/add', element: <AdminBespokeServiceFormPage /> },
            // { path: 'bespoke-services/edit/:id', element: <AdminBespokeServiceFormPage /> },

            // Others
            { path: 'inventory-management', element: <AdminInventoryPage /> },
            { path: 'about-us', element: <AdminAboutUsPage /> },
            { path: 'pages', element: <Navigate to="list" replace /> },
            { path: 'pages/list', element: <AdminStaticPagesPage /> },
            { path: 'pages/add', element: <AdminAddStaticPage /> },
            { path: 'pages/edit/:id', element: <AdminEditStaticPage /> },

            // Size Guides
            { path: 'size-guides', element: <Navigate to="list" replace /> },
            { path: 'size-guides/list', element: <AdminSizeGuidesPage /> },
            { path: 'size-guides/add', element: <AdminAddSizeGuidePage /> },
            { path: 'size-guides/edit/:id', element: <AdminEditSizeGuidePage /> },
            { path: 'error-monitoring', element: <AdminPlaceholderPage /> },

            // Settings
            { path: 'settings/email', element: <AdminEmailSettingsPage /> },
            { path: 'settings/tax', element: <AdminTaxSettingsPage /> },
            { path: 'settings/shipping', element: <AdminShippingSettingsPage /> },
            { path: 'settings/shipping-integration', element: <AdminShippingIntegrationPage /> },
            { path: 'settings/site', element: <AdminSiteSettingsPage /> },

        ],
    },


    // 404 Not Found
    {
        path: '*',
        element: <NotFoundPage />,
    },
];

/**
 * Create browser router with routes configuration
 */
export const router = createBrowserRouter(routes);

export default router;
